import { HomeReview, ResponseStatus, ReviewStatus } from '../types';
import {
  FINDING_SEVERITY_APPEARANCE,
  getFindingSeverityAppearance,
} from '../constants/findingSeverity';
import { dedupeResponsesByItemId, selectBestResponseForItem } from './reviewResponseSelector';

const DEFAULT_PHOTO_API_BASE_URL = 'https://exhalehomereview-api.alexmazure.com/api';

type QuestionField = {
  id?: string;
  name?: string;
  label?: string;
  type?: string;
  unit?: string;
};

type ReportFinding = {
  questionId?: string | null;
  severity?: 'low' | 'medium' | 'high' | 'critical' | null;
  actionability?: 'monitor' | 'planned' | 'near_term' | 'immediate' | null;
};

type ReportItem = {
  id: number;
  name?: string;
  description?: string;
  questionsSchema?: {
    fields?: QuestionField[];
  };
};

type ReportCategory = {
  id: number;
  name?: string;
  items?: ReportItem[];
  subcategories?: ReportCategory[];
};

type ReportResponse = {
  itemId?: number;
  status?: string;
  isPresent?: boolean;
  room?: {
    name?: string;
  };
  responseData?: Record<string, unknown>;
  notes?: string | null;
  item?: ReportItem;
  findings?: ReportFinding[];
  photos?: Array<{
    id?: number | string;
    s3Url?: string;
    uri?: string;
    fileName?: string;
    caption?: string | null;
    note?: string | null;
  }>;
};

export type ReportBuilderReview = HomeReview & {
  memberName?: string | null;
  memberEmail?: string | null;
  homeManagerName?: string | null;
  homeManagerEmail?: string | null;
  onDemandProjects?: string | null;
  responses?: ReportResponse[];
  template?: HomeReview['template'] & {
    categories?: ReportCategory[];
  };
  onDemandItems?: ReportItem[];
};

export interface BuildReviewReportHtmlOptions {
  authToken?: string | null;
  apiBaseUrl?: string;
  forPDF?: boolean;
  generatedAt?: Date;
}

interface ReportStats {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  percentage: number;
}

const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeAttribute = escapeHtml;

const formatDate = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

const severityOrder: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const actionabilityOrder: Record<string, number> = {
  immediate: 0,
  near_term: 1,
  planned: 2,
  monitor: 3,
};

const titleCaseClassification = (value: string | null | undefined): string => {
  if (!value) {
    return 'Unclassified';
  }

  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const compareFindings = (left: ReportFinding, right: ReportFinding): number => {
  const severityDifference =
    (severityOrder[left.severity || ''] ?? Number.MAX_SAFE_INTEGER) -
    (severityOrder[right.severity || ''] ?? Number.MAX_SAFE_INTEGER);
  if (severityDifference !== 0) {
    return severityDifference;
  }

  return (
    (actionabilityOrder[left.actionability || ''] ?? Number.MAX_SAFE_INTEGER) -
    (actionabilityOrder[right.actionability || ''] ?? Number.MAX_SAFE_INTEGER)
  );
};

const findingSeverityKeys = Object.keys(FINDING_SEVERITY_APPEARANCE) as Array<
  keyof typeof FINDING_SEVERITY_APPEARANCE
>;

const normalizeQuestionKey = (questionId: string): string =>
  questionId.replace(/_item_\d+$/, '');

const normalizedQuestionKeys = (questionIds: Array<string | null | undefined>): Set<string> =>
  new Set(
    questionIds
      .filter((questionId): questionId is string => Boolean(questionId))
      .map(normalizeQuestionKey)
  );

const hasRenderedQuestion = (
  renderedQuestionIds: Set<string>,
  questionIds: Array<string | null | undefined>
): boolean =>
  Array.from(normalizedQuestionKeys(questionIds)).some(questionId =>
    renderedQuestionIds.has(questionId)
  );

const rememberRenderedQuestion = (
  renderedQuestionIds: Set<string>,
  questionIds: Array<string | null | undefined>
): void => {
  normalizedQuestionKeys(questionIds).forEach(questionId => renderedQuestionIds.add(questionId));
};

const findingMatchesQuestion = (
  finding: ReportFinding,
  questionIds: Array<string | null | undefined> | null
): boolean => {
  if (questionIds === null) {
    return !finding.questionId;
  }

  if (!finding.questionId) {
    return false;
  }

  return normalizedQuestionKeys(questionIds).has(normalizeQuestionKey(finding.questionId));
};

const matchingFindings = (
  response: ReportResponse,
  questionIds: Array<string | null | undefined> | null
): ReportFinding[] =>
  (response.findings || [])
    .filter(finding => findingMatchesQuestion(finding, questionIds))
    .sort(compareFindings);

const findingSeverityHighlightClasses = (finding: ReportFinding | undefined): string => {
  const severity = findingSeverityKeys.find(candidate => candidate === finding?.severity);
  if (!severity || !getFindingSeverityAppearance(severity)) {
    return '';
  }

  return ` severity-highlight severity-highlight-${severity}`;
};

const mostSevereFinding = (findings: ReportFinding[]): ReportFinding | undefined =>
  findings.find(finding => Boolean(getFindingSeverityAppearance(finding.severity)));

const renderSeverityCss = (): string => findingSeverityKeys.map(severity => {
  const appearance = getFindingSeverityAppearance(severity);
  if (!appearance) {
    return '';
  }

  return `
        .severity-highlight-${severity} {
          background-color: ${appearance.surface};
          border-left-color: ${appearance.border};
          color: ${appearance.foreground};
        }
        .finding-badge.severity-${severity} {
          background-color: ${appearance.surface};
          border-color: ${appearance.border};
          color: ${appearance.foreground};
        }`;
}).join('');

const findQuestion = (item: ReportItem, questionId: string | null | undefined): QuestionField | undefined =>
  questionId
    ? item.questionsSchema?.fields?.find(field => {
      const normalizedQuestionId = normalizeQuestionKey(questionId);
      return (field.id && normalizeQuestionKey(field.id) === normalizedQuestionId) ||
        (field.name && normalizeQuestionKey(field.name) === normalizedQuestionId);
    })
    : undefined;

const questionLabel = (item: ReportItem, questionId: string | null | undefined): string => {
  if (!questionId) {
    return 'Overall item';
  }

  return findQuestion(item, questionId)?.label || questionId;
};

const renderFindingBadges = (finding: ReportFinding): string => {
  const severity = titleCaseClassification(finding.severity);
  const actionability = titleCaseClassification(finding.actionability);

  return `
    <span class="finding-badge severity-${escapeAttribute(finding.severity || 'unclassified')}">${escapeHtml(severity)}</span>
    <span class="finding-badge actionability-${escapeAttribute(finding.actionability || 'unclassified')}">${escapeHtml(actionability)}</span>
  `;
};

const collectCategoryItems = (categories: ReportCategory[] | undefined): ReportItem[] => {
  const items: ReportItem[] = [];

  (categories || []).forEach(category => {
    items.push(...(category.items || []));
    items.push(...collectCategoryItems(category.subcategories));
  });

  return items;
};

const reportItemsById = (review: ReportBuilderReview): Map<string, ReportItem> => {
  const items = [
    ...collectCategoryItems(review.template?.categories),
    ...collectCategoryItems(review.categories as ReportCategory[] | undefined),
    ...(review.onDemandItems || []),
    ...((review.responses || []).map(response => response.item).filter(Boolean) as ReportItem[]),
  ];

  return new Map(items.map(item => [String(item.id), item]));
};

const getResponseByItemId = (
  review: ReportBuilderReview,
  itemId: number
): ReportResponse | undefined =>
  selectBestResponseForItem(review.responses as any, itemId) as ReportResponse | undefined;

const calculateReportStats = (review: ReportBuilderReview): ReportStats => {
  const responses = dedupeResponsesByItemId(review.responses as any) as ReportResponse[];
  const total = responses.length;
  const completed = responses.filter(response => response.status === ResponseStatus.COMPLETED).length;
  const inProgress = responses.filter(response => response.status === ResponseStatus.IN_PROGRESS).length;
  const notStarted = responses.filter(response => response.status === ResponseStatus.NOT_STARTED).length;

  return {
    total,
    completed,
    inProgress,
    notStarted,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

export const formatReportResponseValue = (value: unknown, field?: QuestionField): string => {
  if (field?.type === 'helpful_links') {
    return '';
  }

  if (field?.type === 'dimensions_2d' && typeof value === 'object' && value !== null) {
    const dimensionValue = value as { length?: number | string; width?: number | string };
    const unit = field.unit || 'in';
    return `${dimensionValue.length || 0} x ${dimensionValue.width || 0} ${unit}`;
  }

  if (field?.type === 'dimensions_3d' && typeof value === 'object' && value !== null) {
    const dimensionValue = value as {
      length?: number | string;
      width?: number | string;
      depth?: number | string;
    };
    const unit = field.unit || 'in';
    return `${dimensionValue.length || 0} x ${dimensionValue.width || 0} x ${dimensionValue.depth || 0} ${unit}`;
  }

  if (value === true || value === 'true' || value === 'yes') {
    return 'Yes';
  }

  if (value === false || value === 'false' || value === 'no') {
    return 'No';
  }

  if (Array.isArray(value)) {
    return value.map(entry => String(entry)).join(', ');
  }

  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }

  return String(value ?? '');
};

const buildPhotoUrl = (
  photo: NonNullable<ReportResponse['photos']>[number],
  authToken: string | null | undefined,
  apiBaseUrl: string
): string => {
  const basePhotoUrl = photo.id
    ? `${apiBaseUrl.replace(/\/$/, '')}/upload/photos/${photo.id}`
    : (photo.s3Url || photo.uri || '');

  if (!authToken || !basePhotoUrl || basePhotoUrl.includes('token=')) {
    return basePhotoUrl;
  }

  const separator = basePhotoUrl.includes('?') ? '&' : '?';
  return `${basePhotoUrl}${separator}token=${encodeURIComponent(authToken)}`;
};

const renderPriorityFindings = (review: ReportBuilderReview): string => {
  const itemById = reportItemsById(review);
  const entries = (review.responses || []).flatMap((response, responseIndex) => {
    const item = response.item || itemById.get(String(response.itemId));
    if (!item) {
      return [];
    }

    return (response.findings || []).map((finding, findingIndex) => ({
      finding,
      findingIndex,
      item,
      responseIndex,
    }));
  });

  if (entries.length === 0) {
    return '';
  }

  entries.sort((left, right) =>
    compareFindings(left.finding, right.finding) ||
    left.responseIndex - right.responseIndex ||
    left.findingIndex - right.findingIndex
  );

  return `
    <section class="priority-findings">
      <h2>Priority Findings</h2>
      ${entries.map(({ finding, item }) => `
        <div class="priority-finding${findingSeverityHighlightClasses(finding)}">
          <div class="priority-finding-title">
            <strong>${escapeHtml(item.name || 'Unnamed item')}</strong>
            <span>— ${escapeHtml(questionLabel(item, finding.questionId))}</span>
          </div>
          <div class="finding-badges">${renderFindingBadges(finding)}</div>
        </div>
      `).join('')}
    </section>
  `;
};

const renderInlineFindings = (
  response: ReportResponse,
  item: ReportItem,
  questionId: string | null,
  matchingQuestionIds: Array<string | null | undefined> | null = questionId === null
    ? null
    : [questionId]
): string => {
  const findings = matchingFindings(response, matchingQuestionIds);

  if (findings.length === 0) {
    return '';
  }

  return findings.map(finding => `
    <div class="inline-finding${findingSeverityHighlightClasses(finding)}">
      <span class="detail-label">Finding${questionId ? ` — ${escapeHtml(questionLabel(item, questionId))}` : ''}:</span>
      ${renderFindingBadges(finding)}
    </div>
  `).join('');
};

const renderPhotos = (
  response: ReportResponse,
  options: Required<Pick<BuildReviewReportHtmlOptions, 'apiBaseUrl'>> &
    Pick<BuildReviewReportHtmlOptions, 'authToken'>
): string => {
  if (!response.photos || response.photos.length === 0) {
    return '';
  }

  return `
    <div class="photos">
      <strong>Photos:</strong>
      <div class="photo-grid">
        ${response.photos.map((photo, index) => {
          const photoUrl = buildPhotoUrl(photo, options.authToken, options.apiBaseUrl);
          return `
            <div class="photo-item">
              ${photoUrl
                ? `<img src="${escapeAttribute(photoUrl)}" alt="Photo ${index + 1}" />`
                : '<div class="photo-unavailable">Image unavailable</div>'}
              <div class="photo-label">${escapeHtml(photo.caption || photo.fileName || `Photo ${index + 1}`)}</div>
              ${photo.note ? `<div class="photo-note"><strong>Photo note:</strong> ${escapeHtml(photo.note)}</div>` : ''}
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
};

const renderCompletedItem = (
  review: ReportBuilderReview,
  item: ReportItem,
  response: ReportResponse,
  options: Required<Pick<BuildReviewReportHtmlOptions, 'apiBaseUrl'>> &
    Pick<BuildReviewReportHtmlOptions, 'authToken'>
): string => {
  const itemFinding = mostSevereFinding(matchingFindings(response, null));
  let html = `
    <div class="item">
      <div class="item-header${findingSeverityHighlightClasses(itemFinding)}">
        <div>
          <div class="item-name">${escapeHtml(item.name || 'Unnamed item')}</div>
          ${item.description ? `<div class="item-description">${escapeHtml(item.description)}</div>` : ''}
        </div>
        <span class="status-badge ${response.isPresent ? 'status-present' : 'status-not-present'}">
          ${response.isPresent ? 'Present' : 'Not Present'}
        </span>
      </div>

      <div class="response-details">
  `;

  if (response.room?.name) {
    html += `
      <div class="detail-row">
        <span class="detail-label">Location:</span> ${escapeHtml(response.room.name)}
      </div>
    `;
  }

  html += renderInlineFindings(response, item, null);

  const renderedQuestionIds = new Set<string>();
  if (response.responseData && Object.keys(response.responseData).length > 0) {
    Object.entries(response.responseData).forEach(([key, value]) => {
      const baseQuestionKey = normalizeQuestionKey(key);
      const field = findQuestion(item, baseQuestionKey);
      const questionIds = [key, baseQuestionKey, field?.id, field?.name];
      const questionFinding = mostSevereFinding(matchingFindings(response, questionIds));
      const alreadyRenderedQuestion = hasRenderedQuestion(renderedQuestionIds, questionIds);
      const label = field?.label || key;
      const displayValue = formatReportResponseValue(value, field) || 'Not specified';

      if (displayValue) {
        html += `
          <div class="detail-row${findingSeverityHighlightClasses(questionFinding)}">
            <span class="detail-label">${escapeHtml(label)}:</span> ${escapeHtml(displayValue)}
          </div>
        `;
        if (!alreadyRenderedQuestion) {
          html += renderInlineFindings(
            response,
            item,
            field?.id || field?.name || baseQuestionKey,
            questionIds
          );
        }
        rememberRenderedQuestion(renderedQuestionIds, questionIds);
      }
    });
  }

  (response.findings || []).forEach(finding => {
    if (finding.questionId && !renderedQuestionIds.has(normalizeQuestionKey(finding.questionId))) {
      html += renderInlineFindings(response, item, finding.questionId);
      renderedQuestionIds.add(normalizeQuestionKey(finding.questionId));
    }
  });

  if (response.notes) {
    html += `
      <div class="notes">
        <strong>Notes:</strong> ${escapeHtml(response.notes)}
      </div>
    `;
  }

  html += renderPhotos(response, options);

  html += `
      </div>
    </div>
  `;

  return html;
};

const renderTemplateCategory = (
  review: ReportBuilderReview,
  category: ReportCategory,
  categoryIndex: number,
  options: Required<Pick<BuildReviewReportHtmlOptions, 'apiBaseUrl' | 'forPDF'>> &
    Pick<BuildReviewReportHtmlOptions, 'authToken'>,
  level = 0
): string => {
  const directItems = category.items || [];
  const categoryItems = [...directItems, ...collectCategoryItems(category.subcategories)];
  const completedInCategory = categoryItems.filter(item =>
    getResponseByItemId(review, item.id)?.status === ResponseStatus.COMPLETED
  ).length;

  let html = options.forPDF && level === 0 && categoryIndex > 0
    ? '<div style="page-break-before: always; height: 1px;"></div>'
    : '';

  html += `
    <div class="category ${level > 0 ? 'subcategory' : ''}">
      <h3>${escapeHtml(category.name || 'Unnamed category')} (${completedInCategory}/${categoryItems.length} completed)</h3>
  `;

  directItems.forEach(item => {
    const response = getResponseByItemId(review, item.id);
    const status = response?.status || ResponseStatus.NOT_STARTED;

    if (status === ResponseStatus.COMPLETED && response) {
      html += renderCompletedItem(review, item, response, options);
    }
  });

  (category.subcategories || []).forEach((subcategory, subcategoryIndex) => {
    html += renderTemplateCategory(
      review,
      subcategory,
      subcategoryIndex,
      options,
      level + 1
    );
  });

  html += '</div>';
  return html;
};

const renderOnDemandItems = (
  review: ReportBuilderReview,
  options: Required<Pick<BuildReviewReportHtmlOptions, 'apiBaseUrl'>> &
    Pick<BuildReviewReportHtmlOptions, 'authToken'>
): string => {
  if (!review.onDemandItems || review.onDemandItems.length === 0) {
    return '';
  }

  const completedOnDemand = review.onDemandItems.filter(item =>
    getResponseByItemId(review, item.id)?.status === ResponseStatus.COMPLETED
  ).length;

  let html = `
    <div class="category">
      <h3>Custom Items (${completedOnDemand}/${review.onDemandItems.length} completed)</h3>
  `;

  review.onDemandItems.forEach(item => {
    const response = getResponseByItemId(review, item.id);
    const status = response?.status || ResponseStatus.NOT_STARTED;

    if (status === ResponseStatus.COMPLETED && response) {
      html += renderCompletedItem(review, item, response, options);
      return;
    }

    const statusText = status === ResponseStatus.COMPLETED
      ? 'Completed'
      : status === ResponseStatus.IN_PROGRESS
        ? 'In Progress'
        : 'Not Started';
    const itemFinding = response
      ? mostSevereFinding(matchingFindings(response, null))
      : undefined;

    html += `
      <div class="item">
        <div class="item-header${findingSeverityHighlightClasses(itemFinding)}">
          <span class="item-name">${escapeHtml(item.name || 'Unnamed custom item')}</span>
          <span class="item-status status-${escapeAttribute(status)}">${statusText}</span>
        </div>
        <div class="item-description">${escapeHtml(item.description || 'Custom inspection item')}</div>
    `;

    const renderedQuestionIds = new Set<string>();
    if (response?.responseData) {
      html += '<div class="item-details">';
      Object.entries(response.responseData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const baseQuestionKey = normalizeQuestionKey(key);
          const field = findQuestion(item, baseQuestionKey);
          const questionIds = [key, baseQuestionKey, field?.id, field?.name];
          const questionFinding = mostSevereFinding(matchingFindings(response, questionIds));
          const alreadyRenderedQuestion = hasRenderedQuestion(renderedQuestionIds, questionIds);
          const label = field?.label || key;
          html += `
            <div class="detail-row${findingSeverityHighlightClasses(questionFinding)}">
              <span class="detail-label">${escapeHtml(label)}:</span> ${escapeHtml(formatReportResponseValue(value, field))}
            </div>
          `;
          if (!alreadyRenderedQuestion) {
            html += renderInlineFindings(
              response,
              item,
              field?.id || field?.name || baseQuestionKey,
              questionIds
            );
          }
          rememberRenderedQuestion(renderedQuestionIds, questionIds);
        }
      });
      html += '</div>';
    }

    if (response) {
      (response.findings || []).forEach(finding => {
        if (finding.questionId && !renderedQuestionIds.has(normalizeQuestionKey(finding.questionId))) {
          html += renderInlineFindings(response, item, finding.questionId);
          renderedQuestionIds.add(normalizeQuestionKey(finding.questionId));
        }
      });
      html += renderInlineFindings(response, item, null);
      if (response.notes) {
        html += `<div class="notes"><strong>Notes:</strong> ${escapeHtml(response.notes)}</div>`;
      }
      html += renderPhotos(response, options);
    }

    html += '</div>';
  });

  html += '</div>';
  return html;
};

export function buildReviewReportHtml(
  review: ReportBuilderReview,
  options: BuildReviewReportHtmlOptions = {}
): string {
  const forPDF = options.forPDF ?? false;
  const apiBaseUrl = options.apiBaseUrl || DEFAULT_PHOTO_API_BASE_URL;
  const generatedAt = options.generatedAt || new Date();
  const stats = calculateReportStats(review);
  const submittedDate = formatDate(review.submittedAt);
  const completedDate = formatDate(review.completedAt);
  const address = review.property?.address || 'Unknown Address';
  const templateName = review.template?.name || 'Standard Review';
  const statusLabel = review.status === ReviewStatus.SUBMITTED ? 'Submitted' : 'Completed';
  const memberIdentity = [review.memberName, review.memberEmail].filter(Boolean).join(' · ');
  const managerIdentity = [review.homeManagerName, review.homeManagerEmail].filter(Boolean).join(' · ');
  const reportCategories = review.template?.categories?.length
    ? review.template.categories
    : (review.categories as ReportCategory[] | undefined) || [];

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Review Report - ${escapeHtml(address)}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          ${forPDF ? 'width: 100%; max-width: 1100px;' : 'max-width: 800px;'}
          margin: 0 auto;
          padding: 20px;
          box-sizing: border-box;
        }
        * { box-sizing: border-box; }
        .header {
          background-color: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
        }
        h1 { color: #2c3e50; margin: 0 0 10px 0; }
        h2 {
          color: #34495e;
          border-bottom: 2px solid #3498db;
          padding-bottom: 10px;
          margin-top: 30px;
        }
        h3 {
          color: #34495e;
          margin-top: 20px;
          margin-bottom: 10px;
          page-break-after: avoid;
        }
        .company { color: #7f8c8d; font-size: 18px; }
        .review-contact { margin-top: 5px; color: #4b5563; }
        .status {
          display: inline-block;
          background-color: #27ae60;
          color: white;
          padding: 5px 15px;
          border-radius: 20px;
          font-size: 14px;
          margin: 10px 0;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: ${forPDF ? '15px' : '20px'};
          margin: 20px 0;
          ${forPDF ? 'padding-right: 10px;' : ''}
        }
        .stat-card {
          background-color: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
        }
        .stat-number {
          font-size: 24px;
          font-weight: bold;
          color: #2c3e50;
        }
        .stat-label { color: #7f8c8d; font-size: 14px; }
        .general-notes, .on-demand-projects {
          background-color: #f0f9ff;
          border: 1px solid #bfdbfe;
          padding: 15px;
          border-radius: 8px;
          margin: 20px 0;
        }
        .category {
          margin-bottom: 30px;
          page-break-inside: avoid;
        }
        .subcategory { margin-left: 18px; margin-bottom: 15px; }
        .item {
          background-color: #f8f9fa;
          padding: 15px;
          margin: 10px 0;
          border-radius: 8px;
          page-break-inside: avoid;
        }
        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 10px;
          ${forPDF ? 'gap: 10px;' : ''}
        }
        .item-header > div:first-child { flex: 1; min-width: 0; }
        .item-name { font-weight: bold; color: #2c3e50; }
        .item-description {
          color: #7f8c8d;
          font-size: 14px;
          margin: 5px 0;
        }
        .status-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 12px;
          color: white;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .status-present { background-color: #27ae60; }
        .status-not-present { background-color: #e74c3c; }
        .response-details {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #e0e0e0;
        }
        .detail-row { margin: 5px 0; }
        .detail-label {
          font-weight: bold;
          color: #7f8c8d;
          display: inline-block;
          min-width: 100px;
        }
        .notes {
          background-color: #fff;
          padding: 10px;
          border-left: 3px solid #3498db;
          margin: 10px 0;
        }
        .priority-findings {
          margin: 25px 0;
          page-break-inside: avoid;
        }
        .priority-finding {
          background: #fff7ed;
          border-left: 4px solid #ea580c;
          margin: 8px 0;
          padding: 10px 12px;
        }
        .priority-finding-title { margin-bottom: 6px; }
        .finding-badges { display: flex; gap: 6px; flex-wrap: wrap; }
        .finding-badge {
          display: inline-block;
          border-radius: 10px;
          border: 1px solid transparent;
          padding: 2px 8px;
          margin-right: 5px;
          font-size: 11px;
          font-weight: bold;
          background: #e5e7eb;
          color: #374151;
        }
        .severity-highlight {
          border-left-style: solid;
          border-left-width: 4px;
        }
        .item-header.severity-highlight,
        .detail-row.severity-highlight,
        .inline-finding.severity-highlight {
          border-radius: 6px;
          padding: 7px 9px;
        }
        .severity-highlight .item-name,
        .severity-highlight .item-description,
        .severity-highlight .detail-label {
          color: inherit;
        }
        ${renderSeverityCss()}
        .actionability-immediate { background: #fee2e2; color: #991b1b; }
        .inline-finding { margin: 6px 0; }
        .photos { margin-top: 15px; }
        .photo-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-top: 10px;
        }
        .photo-item { text-align: center; }
        .photo-item img {
          max-width: 100%;
          height: 150px;
          object-fit: cover;
          image-orientation: from-image;
          border-radius: 4px;
          border: 1px solid #ddd;
        }
        .photo-label {
          font-size: 12px;
          color: #7f8c8d;
          margin-top: 5px;
        }
        .photo-note { font-size: 12px; color: #374151; margin-top: 4px; }
        .photo-unavailable {
          height: 80px;
          border: 1px dashed #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
        }
        ${forPDF ? `
        @page { margin: 15mm; }
        .category, .item, .stats-grid { page-break-inside: avoid; }
        h3 { page-break-after: avoid; }
        ` : ''}
        @media print {
          body {
            margin: 0;
            padding: 10px;
          }
          .category, .item { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${escapeHtml(address)}</h1>
        <div class="company">${escapeHtml(templateName)}</div>
        <div class="status">${statusLabel}</div>
        <div>${submittedDate ? `Submitted: ${escapeHtml(submittedDate)}` : completedDate ? `Completed: ${escapeHtml(completedDate)}` : ''}</div>
        ${memberIdentity ? `<div class="review-contact"><strong>Member:</strong> ${escapeHtml(memberIdentity)}</div>` : ''}
        ${managerIdentity ? `<div class="review-contact"><strong>Home Manager:</strong> ${escapeHtml(managerIdentity)}</div>` : ''}
      </div>

      <h2>Overall Statistics</h2>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-number">${stats.completed}</div>
          <div class="stat-label">Completed</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${stats.inProgress}</div>
          <div class="stat-label">In Progress</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${stats.notStarted}</div>
          <div class="stat-label">Not Started</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${stats.percentage}%</div>
          <div class="stat-label">Complete</div>
        </div>
      </div>
  `;

  if (review.notes) {
    html += `
      <div class="general-notes">
        <h3>General Notes</h3>
        <p>${escapeHtml(review.notes)}</p>
      </div>
    `;
  }

  if (review.onDemandProjects) {
    html += `
      <div class="on-demand-projects">
        <h3>On Demand Projects</h3>
        <p>${escapeHtml(review.onDemandProjects)}</p>
      </div>
    `;
  }

  html += renderPriorityFindings(review);

  html += '<h2>Review Items</h2>';

  reportCategories.forEach((category, categoryIndex) => {
    html += renderTemplateCategory(review, category, categoryIndex, {
      apiBaseUrl,
      authToken: options.authToken,
      forPDF,
    });
  });

  html += renderOnDemandItems(review, {
    apiBaseUrl,
    authToken: options.authToken,
  });

  html += `
      <div style="margin-top: 50px; text-align: center; color: #7f8c8d; font-size: 12px;">
        Generated on ${escapeHtml(generatedAt.toLocaleString())}
      </div>
    </body>
    </html>
  `;

  return html;
}
